import { EBool } from '~/src/dash/api/types/bool';
import {
  ECampaignDeliveryStatus,
  EDebugPrebidData,
} from '~/src/dash/components/filters/options/campaigns';
import { OrderDirectionType } from '~/src/dash/components/tables/common';
import { createUrl, restPatch, restPost } from '~/src/dash/api/rest';
import { BidErrorKey, CampaignNoFillErrorKey } from '~/src/dash/utils/bidErrorCodes';

export type TLineItemListOrderByKey =
  | 'active'
  | 'advertiser_name'
  | 'end_date'
  | 'impressions'
  | 'name'
  | 'order_name'
  | 'pacing'
  | 'start_date'
  | 'status';

export interface ILineItemsListQueryParams {
  publisher_id: string;
  search: string; // back end will search on name, id
  page_size: number;
  page: number;
  order_direction: OrderDirectionType; // 'asc' | 'desc'
  active: EBool;
  status: ECampaignDeliveryStatus[];
  timezone: string;
  start_date_from: string; // 'YYYY-MM-DD'
  start_date_to: string; // 'YYYY-MM-DD'
  end_date_from: string; // 'YYYY-MM-DD'
  end_date_to: string; // 'YYYY-MM-DD'
  debug: EDebugPrebidData; // 0 = Show All, 1 = Not Low Pacing, 2 = Site Weight Enabled
  advertiser_ids: number[];
  order_ids: number[];
  order_by: TLineItemListOrderByKey;
  get_all?: boolean;
}

export interface ISimpleLineItem {
  ID: number;
  Name: string;
  Active: EBool;
  Status: ECampaignDeliveryStatus;
  StartDate: string; // YYYY-MM-DD
  EndDate: string; // YYYY-MM-DD
  OrderID: number;
  OrderName: string;
  AdvertiserName: string;
  Impressions: number;
  Pacing: string;
  Goal: number;
  CPM: number;
  Revenue: number;
  CurrencySymbol: string;
}

export interface ISimpleLineItemRaw {
  ID: number;
  Name: string;
  Active: EBool;
  Status: ECampaignDeliveryStatus;
  StartDate: string; // YYYY-MM-DD
  EndDate: string; // YYYY-MM-DD
  OrderID: number;
  OrderName: string;
  AdvertiserName: string;
  DeliveredImps: number;
  Pacing: string;
  Goal: number;
  CPM: number;
  Revenue: number;
  CurrencySymbol: string;
  UpdatedAt: string;
}

interface ILineItemsListResponse {
  campaigns: ISimpleLineItem[];
  totalCount: number;
}

interface ILineItemsListResponseRaw {
  campaigns: ISimpleLineItemRaw[];
  totalCount: number;
}

export async function getLineItemsList(
  publisherID: string,
  query: ILineItemsListQueryParams,
): Promise<ILineItemsListResponse> {
  const reqUrl = `${createUrl('/campaigns-v2', publisherID, 'v1')}`;
  const response = await restPost<ILineItemsListResponse>(reqUrl, query);
  return (response?.parsedBody as ILineItemsListResponse) || {};
}

export async function getLineItemsListRawNoPagination(
  publisherID: string,
  query: ILineItemsListQueryParams,
): Promise<ILineItemsListResponseRaw> {
  const reqUrl = `${createUrl('/campaigns-v2', publisherID, 'v1')}`;
  query.get_all = true;
  const response = await restPost<ILineItemsListResponseRaw>(reqUrl, query);
  return (response?.parsedBody as ILineItemsListResponseRaw) || {};
}

interface IPatchLineItemActiveStatusResponse {
  ID: number;
  Active: EBool;
  Status: ECampaignDeliveryStatus;
}

export interface IPatchLineItemActiveStatusQueryParams {
  publisher_id: string;
  id: number;
  active: EBool;
}

export async function updateLineItemActiveStatus(
  publisherID: string,
  query: IPatchLineItemActiveStatusQueryParams,
): Promise<IPatchLineItemActiveStatusResponse> {
  const reqUrl = `${createUrl('/campaign/toggle_active', publisherID, 'v1')}`;
  const response = await restPatch<IPatchLineItemActiveStatusResponse>(reqUrl, query);
  return response?.parsedBody as IPatchLineItemActiveStatusResponse;
}

export type TOrderListOrderByKey =
  | 'active'
  | 'advertiser_name'
  | 'end_date'
  | 'impressions'
  | 'name'
  | 'trafficker_name'
  | 'start_date'
  | 'line_item_status';

export interface IOrdersListQueryParams {
  publisher_id: string;
  search: string; // back end will search on name, id
  page_size: number;
  page: number;
  order_direction: OrderDirectionType; // 'asc' | 'desc'
  active: EBool;
  line_item_status: ECampaignDeliveryStatus[];
  timezone: string;
  start_date_from: string; // 'YYYY-MM-DD'
  start_date_to: string; // 'YYYY-MM-DD'
  end_date_from: string; // 'YYYY-MM-DD'
  end_date_to: string; // 'YYYY-MM-DD'
  advertiser_ids: number[];
  trafficker_ids: number[];
  order_by: TOrderListOrderByKey;
}

export interface ISimpleOrder {
  ID: number;
  Name: string;
  Active: EBool;
  StartDate: string; // YYYY-MM-DD
  EndDate: string; // YYYY-MM-DD
  AdvertiserID: number;
  AdvertiserName: string;
  TraffickerID: number;
  TraffickerName: string;
  CampaignStatuses: {
    Draft: number;
    Untargeted: number;
    InvalidMediafile: number;
    Ready: number;
    Upcoming: number;
    Delivering: number;
    DeliveryGoalMet: number;
    Completed: number;
    Inactive: number;
  };
  Impressions: number;
  NumCampaigns: number;
}

interface IOrdersListResponse {
  Orders: ISimpleOrder[];
  TotalCount: number;
}

export async function getOrdersList(
  publisherID: string,
  query: IOrdersListQueryParams,
): Promise<IOrdersListResponse> {
  const reqUrl = `${createUrl('/orders', publisherID, 'v1')}`;
  const response = await restPost<IOrdersListResponse>(reqUrl, query);
  return response?.parsedBody as IOrdersListResponse;
}

interface IPatchOrderActiveStatusResponse {
  ID: number;
  Active: EBool;
}

export interface IPatchOrderActiveStatusQueryParams {
  publisher_id: string;
  active: EBool;
}

export async function patchOrderActiveStatus(
  publisherID: string,
  id: number,
  query: IPatchOrderActiveStatusQueryParams,
): Promise<IPatchOrderActiveStatusResponse> {
  const reqUrl = `${createUrl(`/orders/${id}/toggle_active`, publisherID, 'v1')}`;
  const response = await restPatch<IPatchOrderActiveStatusResponse>(reqUrl, query);
  return response?.parsedBody as IPatchOrderActiveStatusResponse;
}

type AuctionErrorKey = BidErrorKey | CampaignNoFillErrorKey;

export interface AuctionInsightsNode {
  name: string;
  total: number;
  percentage: number;
  values: Record<AuctionErrorKey, number>;
}

export type AuctionInsightKey =
  | 'TotalTargetedRequests'
  | 'TotalAdserverFilled'
  | 'TotalPacingRejected'
  | 'TotalBidResponse'
  | 'TotalInvalidCreatives'
  | 'TotalAuctionBids'
  | 'TotalBidResponseErrors'
  | 'TotalNoBid'
  | 'TotalBidTimeout'
  | 'TotalValidBids'
  | 'TotalInvalidBids'
  | 'TotalBidWon'
  | 'TotalBidLost'
  | 'TotalValidBidsRejected'
  | 'TotalBidderImpression'
  | 'TotalBidderNoImpression';

interface AuctionInsightsLink {
  source: AuctionInsightKey;
  target: AuctionInsightKey;
  value: number;
}

export interface AuctionInsightsData {
  nodes: Record<AuctionInsightKey, AuctionInsightsNode>;
  links: AuctionInsightsLink[];
}

export interface IGetCampaignAuctionInsightsQueryParams {
  PublisherID: number;
  CampaignID: number;
  StartDate: string;
  EndDate: string;
  Timezone: string;
}

export async function getCampaignAuctionInsights(
  publisherID: string,
  query: unknown,
): Promise<unknown> {
  const reqUrl = `${createUrl('/campaign_auction_insights', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query);
  return (response?.parsedBody as { Insights: unknown })?.Insights || {};
}
